import { formatDateForInput } from "@/lib/dateFormat";

// Shared types and helpers for the Enquiries pages. Field names match what
// GET /api/enquiries and GET /api/enquiries/{id} actually return.

export interface EnquiryQuotation {
  id: number;
  quotation_number?: string | null;
  status: string;
  total_amount?: number | string | null;
  created_at?: string;
}

export interface EnquiryData {
  id: number;
  client_id?: number | null;
  client_name: string;
  client_phone: string;
  client_email?: string | null;
  /** Event type */
  type?: string | null;
  event_date?: string | null;
  guest_count?: number | null;
  budget?: number | string | null;
  status: string;
  notes?: string | null;
  lead_source?: string | null;
  created_at?: string;
  client?: { id: number; name: string; phone?: string | null; email?: string | null } | null;
  /** Newest first */
  quotations?: EnquiryQuotation[];
  event?: { id: number; name: string; status: string; start_date?: string } | null;
}

export type EnquiryStatus = "new" | "qualified" | "converted" | "rejected";

export const STATUS_LABEL: Record<string, string> = {
  new: "New",
  qualified: "Qualified",
  converted: "Converted",
  rejected: "Rejected",
};

export const statusLabel = (s: string) => STATUS_LABEL[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : "-");

export const enquiryName = (e: EnquiryData) => e.client_name || e.client?.name || "Unknown";
export const enquiryPhone = (e: EnquiryData) => e.client_phone || e.client?.phone || "";
export const enquiryEmail = (e: EnquiryData) => e.client_email || e.client?.email || "";
export const latestQuotation = (e: EnquiryData): EnquiryQuotation | undefined => e.quotations?.[0];

/** YYYY-MM-DD in local time, for date inputs and exports (handles plain dates and ISO timestamps) */
export const ymd = (d?: string | null) => formatDateForInput(d);

const digits = (s: string) => s.replace(/\D+/g, "");

/** tel: link, or "" when there is no usable number */
export const telHref = (phone: string) => {
  const d = digits(phone);
  if (d.length < 6) return "";
  return `tel:${phone.trim().startsWith("+") ? "+" : ""}${d}`;
};

/** wa.me link; a plain 10-digit Indian mobile gets the 91 country code */
export const whatsappHref = (phone: string) => {
  let d = digits(phone);
  if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  if (d.length === 10) d = `91${d}`;
  return d.length >= 11 ? `https://wa.me/${d}` : "";
};

/** Name, phone (digits only, so "98765 43210" finds "9876543210"), email, event type, lead source */
export function matchesSearch(e: EnquiryData, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const text = [enquiryName(e), enquiryEmail(e), enquiryPhone(e), e.type, e.lead_source, e.client?.name]
    .map((v) => (v || "").toLowerCase());
  if (text.some((v) => v.includes(q))) return true;
  const qd = digits(q);
  return qd.length >= 3 && [enquiryPhone(e), e.client?.phone || ""].some((p) => digits(p).includes(qd));
}
